import { forwardRef, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectEditRedlineComment,
  selectHasZoomedRecently,
  selectSelectedBlacklineSegment,
  setSelectedBlacklineSegment,
} from '../../../redux/viewer-slice'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { DocumentBlacklineSegment } from '../../../shared/interfaces/project/document/blackline/document-blackline.interface'
import DiffCommentComponent from '../../overlay/diff-comment-component'
import DiffCreateComment from '../../overlay/diff-create-comment'

const newBlacklineSegmentID = 'new_blackline_segment'

interface RedlineCreateSidelingProps {
  documentBlacklineSegment: DocumentBlacklineSegment
  top?: string
}

const DocumentViewerRedlineCard = forwardRef<
  HTMLDivElement,
  RedlineCreateSidelingProps
>(({ documentBlacklineSegment, top }, ref) => {
  const selectedBlacklineSegment = useSelector(selectSelectedBlacklineSegment)
  const hasZoomedRecently = useSelector(selectHasZoomedRecently)
  const editRedlineComment = useSelector(selectEditRedlineComment)
  const dispatch = useDispatch()

  const [isCommentsExpanded, setIsCommentsExpanded] = useState<boolean>(false)

  const onSelectBlacklineSegment = useCallback(() => {
    if (!documentBlacklineSegment) {
      return
    }
    dispatch(setSelectedBlacklineSegment(documentBlacklineSegment))
  }, [dispatch, documentBlacklineSegment])

  // const { documentId } = useParams()
  // const [deleteBlacklineSegment] = useDeleteBlacklineSegmentMutation()

  const onClickCommentsExpand = useCallback((e) => {
    e.stopPropagation()
    setIsCommentsExpanded((prev) => !prev)
  }, [])

  const remainingCommentCount = useMemo(() => {
    if (!documentBlacklineSegment?.comments?.length) {
      return 0
    }
    if (
      documentBlacklineSegment?.comments?.length >= 0 &&
      documentBlacklineSegment?.comments?.length <= 2
    ) {
      return 0
    }
    return documentBlacklineSegment?.comments?.length - 2
  }, [documentBlacklineSegment])

  const documentBlacklineSegmentContent = useMemo(() => {
    return (
      <div className="m-1 flex flex-col">
        {documentBlacklineSegment?.comments?.[0] ? (
          <DiffCommentComponent
            documentBlacklineSegmentComment={
              documentBlacklineSegment?.comments?.[0]
            }
            segment={documentBlacklineSegment}
          />
        ) : null}
        <div className={`border-t py-1`}>
          {remainingCommentCount > 0 ? (
            <button
              onClick={onClickCommentsExpand}
              data-cy="revision-comments-expand-button"
              className={
                'flex w-full items-center justify-start gap-1 border-y p-1 px-2 text-xs text-gray-500 hover:bg-gray-100'
              }
            >
              <span>
                {isCommentsExpanded ? 'Hide' : 'Show'} ({remainingCommentCount})
                Comment(s)
              </span>
              {isCommentsExpanded ? (
                <ChevronUpIcon width={18} />
              ) : (
                <ChevronDownIcon width={18} />
              )}
            </button>
          ) : null}
          {isCommentsExpanded &&
            documentBlacklineSegment?.comments?.map((comment, index) => {
              if (
                index === 0 ||
                index === (documentBlacklineSegment?.comments?.length ?? 0) - 1
              ) {
                return null
              }
              return (
                <div key={comment.id}>
                  <DiffCommentComponent
                    documentBlacklineSegmentComment={comment}
                    segment={documentBlacklineSegment}
                  />
                </div>
              )
            })}
          {documentBlacklineSegment?.comments?.[
            documentBlacklineSegment?.comments?.length - 1
          ] && documentBlacklineSegment?.comments?.length >= 2 ? (
            <DiffCommentComponent
              documentBlacklineSegmentComment={
                documentBlacklineSegment?.comments?.[
                  documentBlacklineSegment?.comments?.length - 1
                ]
              }
              segment={documentBlacklineSegment}
            />
          ) : null}
        </div>
      </div>
    )
  }, [
    documentBlacklineSegment,
    remainingCommentCount,
    onClickCommentsExpand,
    isCommentsExpanded,
  ])

  const commentBeingEdited = useMemo(() => {
    return documentBlacklineSegment?.comments?.find(
      (comment) => comment.id === editRedlineComment
    )
  }, [documentBlacklineSegment?.comments, editRedlineComment])

  return (
    <div
      data-id={documentBlacklineSegment?.id ?? newBlacklineSegmentID}
      ref={ref}
      id={
        documentBlacklineSegment?.id?.toString() ??
        newBlacklineSegmentID.toString()
      }
      className={`revision-sideling absolute mb-2 ml-4 rounded pr-2 ${hasZoomedRecently && selectedBlacklineSegment?.id !== documentBlacklineSegment?.id ? '' : 'transition-[top]'} ${selectedBlacklineSegment?.id === documentBlacklineSegment?.id ? 'z-20 border-l-2 border-gray-500' : 'z-10 border-l hover:border-gray-500'}`}
      style={{
        top,
        opacity:
          hasZoomedRecently &&
          selectedBlacklineSegment?.id !== documentBlacklineSegment?.id
            ? 0
            : 1,
      }}
    >
      <div
        onClick={onSelectBlacklineSegment}
        className={`relative w-[450px] cursor-pointer border border-gray-300 bg-white text-left text-sm ${selectedBlacklineSegment?.id === documentBlacklineSegment?.id ? 'shadow-lg' : ''}`}
      >
        {documentBlacklineSegmentContent}
        {!commentBeingEdited ? (
          <DiffCreateComment segment={documentBlacklineSegment} />
        ) : null}
      </div>
    </div>
  )
})

DocumentViewerRedlineCard.displayName = 'RedlineCreateSideling'

export default DocumentViewerRedlineCard
